import { useEffect, useRef } from "react"
import { X, ChevronRight, AlertTriangle, Lightbulb } from "lucide-react"
import { cn } from "@/lib/utils"
import type { ProactiveMessage } from "./types"

interface ConciergeProactiveTeaserProps {
  message: ProactiveMessage
  onDismiss: () => void
  onOpen: () => void
}

export function ConciergeProactiveTeaser({
  message,
  onDismiss,
  onOpen,
}: ConciergeProactiveTeaserProps) {
  const isAlert = message.badge_type === "alert"
  const isHighPriority = message.priority === "high"
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Auto-dismiss after 30s
  useEffect(() => {
    timerRef.current = setTimeout(onDismiss, 30_000)
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [onDismiss])

  return (
    <div
      className={cn(
        "fixed bottom-24 right-6 z-[99]",
        "max-w-[320px] bg-card/90 backdrop-blur-sm border border-border/50 rounded-xl",
        "shadow-lg overflow-hidden p-4",
        "animate-in slide-in-from-bottom-3 fade-in duration-300",
        "max-md:right-4 max-md:left-4 max-md:max-w-none",
      )}
    >
      {/* Left accent bar */}
      <div className={cn(
        "absolute top-0 left-0 w-1.5 h-full rounded-l-xl",
        isHighPriority ? "bg-destructive" : "bg-primary",
      )} />

      {/* Header */}
      <div className="flex items-start justify-between mb-2">
        <span
          className={cn(
            "inline-flex items-center gap-1",
            "text-[10px] font-semibold uppercase tracking-wider",
            "px-2 py-0.5 rounded",
            isAlert ? "bg-warning/10 text-warning" : "bg-teal-50 text-primary",
          )}
        >
          {isAlert ? (
            <AlertTriangle className="h-2.5 w-2.5" />
          ) : (
            <Lightbulb className="h-2.5 w-2.5" />
          )}
          {isAlert ? "Action needed" : "Insight"}
        </span>
        <button
          type="button"
          onClick={onDismiss}
          className="p-0.5 rounded text-muted-foreground hover:bg-gray-100 bg-transparent border-0 cursor-pointer transition-colors"
          aria-label="Dismiss"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Title */}
      <div className="text-sm font-semibold leading-snug mb-1">{message.title}</div>

      {/* Body */}
      <p className="text-[13px] text-muted-foreground leading-relaxed mb-3">{message.body}</p>

      {/* CTA */}
      <button
        type="button"
        onClick={onOpen}
        className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:text-primary/80 bg-transparent border-0 cursor-pointer p-0 transition-colors"
      >
        {message.cta}
        <ChevronRight className="h-3.5 w-3.5" />
      </button>
    </div>
  )
}
